import { createContext, useContext, useEffect, useState } from 'react';
import {
  authApi, getToken, setToken, clearSession, getBranchId, setBranchId,
} from './api';
import { DocumentLocalizer } from './i18n';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranch] = useState(getBranchId());
  const [loading, setLoading] = useState(Boolean(getToken()));
  const [locale, setLocale] = useState('th');

  // Rehydrate on reload. The token lives in sessionStorage, so this only ever
  // runs within the same tab that logged in.
  useEffect(() => {
    if (!getToken()) return;
    let cancelled = false;

    authApi
      .me()
      .then((data) => {
        if (cancelled) return;
        setUser(data.user);
        setLocale(data.user?.locale || 'th');
        setOrganization(data.organization);
        setBranches(data.branches);
        if (!getBranchId() && data.branches.length) {
          const fallback = data.branches.find((b) => b.isDefault) || data.branches[0];
          setBranchId(fallback.id);
          setBranch(fallback.id);
        }
      })
      .catch(() => clearSession())
      .finally(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, []);

  /** Re-reads the session — the โปรไฟล์ page calls this after บันทึก so the
      header picks up a renamed user without a reload. */
  const refresh = async () => {
    const data = await authApi.me();
    setUser(data.user);
    setLocale(data.user?.locale || 'th');
    setOrganization(data.organization);
    setBranches(data.branches);
    return data;
  };

  const login = async (identifier, password) => {
    const data = await authApi.login(identifier, password);
    setToken(data.token);
    setBranchId(data.defaultBranchId);
    setUser(data.user);
    setLocale(data.user?.locale || 'th');
    setOrganization(data.organization || null);
    setBranches(data.branches);
    setBranch(data.defaultBranchId);
    return data;
  };

  const logout = () => {
    clearSession();
    setUser(null);
    setOrganization(null);
    setBranches([]);
    setBranch(null);
    setLocale('th');
  };

  const switchBranch = (id) => {
    setBranchId(id);
    setBranch(id);
  };

  const switchOrganization = async (organizationId) => {
    const data = await authApi.switchOrganization(organizationId);
    setToken(data.token);
    setBranchId(data.defaultBranchId);
    setUser(data.user);
    setLocale(data.user?.locale || 'th');
    setOrganization(data.organization);
    setBranches(data.branches);
    setBranch(data.defaultBranchId);
    return data;
  };

  const switchLocale = async (nextLocale) => {
    const previous = locale;
    setLocale(nextLocale);
    setUser((current) => (current ? { ...current, locale: nextLocale } : current));
    try {
      await authApi.updateLocale(nextLocale);
    } catch (error) {
      setLocale(previous);
      setUser((current) => (current ? { ...current, locale: previous } : current));
      throw error;
    }
  };

  const value = {
    user,
    organization,
    branches,
    branchId,
    branch: branches.find((b) => b.id === branchId) || null,
    loading,
    locale,
    isAuthenticated: Boolean(user),
    login,
    logout,
    refresh,
    switchBranch,
    switchOrganization,
    switchLocale,
  };

  return (
    <AuthContext.Provider value={value}>
      <DocumentLocalizer locale={locale}>{children}</DocumentLocalizer>
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
